/**
 * Cosmetics v3 "You may also like" carousel.
 *
 * Automatic mode fetches Shopify's product recommendations for the current product and renders the cards.
 * Manual and collection modes arrive already rendered by Liquid. The track is looped with presentation
 * clones at both ends; after a transition the position is normalised without animation, so the motion
 * always continues in the same direction. Add to cart posts to the store's cart endpoint and reports
 * success only after Shopify confirms it, then hands the update to the existing cart drawer.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const instances = new WeakMap();
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

function breakpoint() {
  if (window.innerWidth >= 990) return 'desktop';
  if (window.innerWidth >= 750) return 'tablet';
  return 'mobile';
}

function visibleCount(root) {
  const key = { desktop: 'visibleDesktop', tablet: 'visibleTablet', mobile: 'visibleMobile' }[breakpoint()];
  return Math.max(1, Number(root.dataset[key]) || 1);
}

function formatMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  const whole = String(Math.floor(cents / 100));
  return (format || '{{amount}}')
    .replace('{{amount_no_decimals_with_comma_separator}}', whole)
    .replace('{{amount_with_comma_separator}}', amount)
    .replace('{{amount_no_decimals}}', whole)
    .replace('{{amount}}', amount);
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Builds one card from a recommendations JSON product. Mirrors snippets/cv3-prc-card.liquid. */
function buildCard(root, product) {
  const d = root.dataset;
  const available = product.available !== false;
  const item = el('li', 'cv3-prc__item' + (available ? '' : ' cv3-prc__item--sold-out'));
  item.dataset.productId = String(product.id);

  const media = el('a', 'cv3-prc__media');
  media.href = product.url;
  if (product.featured_image) {
    const img = el('img', 'cv3-prc__image');
    img.src = product.featured_image.includes('?') ? product.featured_image : product.featured_image + '?width=440';
    img.alt = product.title;
    img.loading = 'lazy';
    img.width = 110;
    img.height = 138;
    media.append(img);
  }
  item.append(media);

  const body = el('div', 'cv3-prc__body');
  const title = el('a', 'cv3-prc__title', product.title);
  title.href = product.url;
  body.append(title);
  if (d.showType === 'true' && product.type) body.append(el('p', 'cv3-prc__type', product.type));
  if (d.showVendor === 'true' && product.vendor) body.append(el('p', 'cv3-prc__vendor', product.vendor));
  if (d.showPrice === 'true' && product.price !== undefined) {
    body.append(el('p', 'cv3-prc__price', formatMoney(product.price, d.moneyFormat)));
  }

  if (!available) {
    body.append(el('p', 'cv3-prc__sold-out', d.soldOut));
  } else if (d.showAdd === 'true') {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const single = variants.length === 1 && variants[0].available !== false;
    if (single) {
      const button = el('button', 'cv3-prc__add', d.addLabel);
      button.type = 'button';
      button.dataset.cv3PrcAdd = '';
      button.dataset.variantId = String(variants[0].id);
      button.setAttribute('aria-label', d.addLabelAria.replace('__TITLE__', product.title));
      body.append(button);
    } else {
      const link = el('a', 'cv3-prc__add', d.addLabel);
      link.href = product.url;
      link.setAttribute('aria-label', d.chooseLabelAria.replace('__TITLE__', product.title));
      body.append(link);
    }
  }
  item.append(body);
  return item;
}

class Carousel {
  constructor(root) {
    this.root = root;
    this.carousel = root.querySelector('[data-cv3-prc-carousel]');
    this.viewport = root.querySelector('[data-cv3-prc-viewport]');
    this.track = root.querySelector('[data-cv3-prc-track]');
    this.prev = root.querySelector('[data-cv3-prc-prev]');
    this.next = root.querySelector('[data-cv3-prc-next]');
    this.pauseButton = root.querySelector('[data-cv3-prc-pause]');
    this.controller = new AbortController();
    this.timer = null;
    this.resumeTimer = null;
    this.userPaused = false;
    this.position = 0;
    this.originals = [];
    this.clonesActive = false;
    this.rtl = document.documentElement.dir === 'rtl';
    this.resizeObserver = null;
    this.bound = false;
  }

  get signal() {
    return this.controller.signal;
  }

  init() {
    if (!this.track) return;
    this.originals = [...this.track.querySelectorAll(':scope > .cv3-prc__item')];
    this.teardownClones();
    const count = this.originals.length;
    const visible = visibleCount(this.root);
    const canMove = count > visible;

    if (this.carousel) this.carousel.hidden = count === 0;
    if (count === 0) return;

    if (!canMove) {
      this.setControlsVisible(false);
      this.track.style.transform = '';
      return;
    }

    this.buildClones();
    this.setControlsVisible(true);
    this.position = count;
    this.applyPosition(false);

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.layoutChanged());
      this.resizeObserver.observe(this.viewport);
    }
    if (!this.bound) {
      this.bound = true;
      this.bindControls();
    }
    this.scheduleAutoplay();
  }

  buildClones() {
    const count = this.originals.length;
    const before = this.originals.map((node) => this.cloneNode(node));
    const after = this.originals.map((node) => this.cloneNode(node));
    before.reverse().forEach((node) => this.track.prepend(node));
    after.forEach((node) => this.track.append(node));
    this.clonesActive = true;
    this.count = count;
  }

  cloneNode(node) {
    const clone = node.cloneNode(true);
    clone.dataset.cv3PrcClone = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
    return clone;
  }

  teardownClones() {
    this.track.querySelectorAll('[data-cv3-prc-clone]').forEach((node) => node.remove());
    this.clonesActive = false;
  }

  setControlsVisible(visible) {
    if (this.prev) this.prev.hidden = !visible;
    if (this.next) this.next.hidden = !visible;
    if (this.pauseButton) {
      this.pauseButton.hidden = !visible || this.root.dataset.autoplay !== 'true';
    }
  }

  stepSize() {
    const nodes = this.track.querySelectorAll(':scope > .cv3-prc__item');
    if (nodes.length < 2) return nodes[0] ? nodes[0].getBoundingClientRect().width : 0;
    // offsetLeft runs left-to-right even in RTL, so the difference can be negative.
    return Math.abs(nodes[1].offsetLeft - nodes[0].offsetLeft);
  }

  applyPosition(animate) {
    const duration = animate && !REDUCED.matches ? Number(this.root.dataset.transition) || 450 : 0;
    this.track.style.transition = duration ? `transform ${duration}ms ease` : 'none';
    const offset = this.position * this.stepSize();
    this.track.style.transform = `translate3d(${this.rtl ? offset : -offset}px, 0, 0)`;
  }

  // After a move, keep the position inside the middle copy. The clone and the original look identical,
  // so the jump is invisible and the next move continues in the same direction.
  normalize() {
    const count = this.count;
    if (this.position >= count * 2) {
      this.position -= count;
      this.applyPosition(false);
    } else if (this.position < count) {
      this.position += count;
      this.applyPosition(false);
    }
  }

  move(delta) {
    if (!this.clonesActive) return;
    this.normalize();
    this.position += delta;
    this.applyPosition(true);
    this.track.addEventListener('transitionend', () => this.normalize(), { once: true, signal: this.signal });
  }

  layoutChanged() {
    if (!this.clonesActive) {
      this.init();
      return;
    }
    const shouldMove = this.originals.length > visibleCount(this.root);
    if (!shouldMove) {
      this.init();
      return;
    }
    this.applyPosition(false);
  }

  manual(delta) {
    this.pauseFor(Number(this.root.dataset.interval) * 1000 || 5000);
    this.move(delta);
  }

  pauseFor(ms) {
    clearTimeout(this.resumeTimer);
    this.stopAutoplay();
    this.resumeTimer = setTimeout(() => this.scheduleAutoplay(), ms);
  }

  autoplayAllowed() {
    return this.root.dataset.autoplay === 'true'
      && !REDUCED.matches
      && !this.userPaused
      && !this.hoverPaused
      && !this.focusPaused
      && !document.hidden
      && this.clonesActive;
  }

  scheduleAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayAllowed()) return;
    const interval = (Number(this.root.dataset.interval) || 5) * 1000;
    this.timer = setTimeout(() => {
      this.move(1);
      this.scheduleAutoplay();
    }, interval);
  }

  stopAutoplay() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  bindControls() {
    const { signal } = this;
    this.prev?.addEventListener('click', () => this.manual(-1), { signal });
    this.next?.addEventListener('click', () => this.manual(1), { signal });
    this.pauseButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      const label = this.pauseButton.querySelector('.cv3-prc__pause-label');
      if (label) label.textContent = this.userPaused ? this.root.dataset.play : this.root.dataset.pause;
      if (this.userPaused) this.stopAutoplay(); else this.scheduleAutoplay();
    }, { signal });
    if (this.root.dataset.pauseHover === 'true') {
      this.carousel.addEventListener('mouseenter', () => { this.hoverPaused = true; this.stopAutoplay(); }, { signal });
      this.carousel.addEventListener('mouseleave', () => { this.hoverPaused = false; this.scheduleAutoplay(); }, { signal });
    }
    if (this.root.dataset.pauseFocus === 'true') {
      this.carousel.addEventListener('focusin', (event) => {
        if (!event.target.matches(':focus-visible')) return;
        this.focusPaused = true;
        this.stopAutoplay();
      }, { signal });
      this.carousel.addEventListener('focusout', () => { this.focusPaused = false; this.scheduleAutoplay(); }, { signal });
    }
    document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), { signal });
    this.carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.manual(this.rtl ? -1 : 1);
      if (event.key === 'ArrowLeft') this.manual(this.rtl ? 1 : -1);
    }, { signal });
    this.bindSwipe();
  }

  bindSwipe() {
    let startX = null;
    let startY = null;
    this.viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      startX = event.clientX;
      startY = event.clientY;
    }, { signal: this.signal, passive: true });
    this.viewport.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      startX = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      const towardNext = dx < 0;
      const delta = towardNext !== this.rtl ? 1 : -1;
      this.manual(delta);
    }, { signal: this.signal, passive: true });
  }

  destroy() {
    this.stopAutoplay();
    clearTimeout(this.resumeTimer);
    this.controller.abort();
    this.resizeObserver?.disconnect();
    this.teardownClones();
  }
}

/** Automatic mode: fetch recommendations for the current product and render cards into the track. */
async function loadRecommendations(root) {
  const productId = root.dataset.productId;
  if (!productId) return 0;
  const limit = Number(root.dataset.limit) || 4;
  const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`;
  const track = root.querySelector('[data-cv3-prc-track]');
  if (!track) return 0;
  track.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
    const data = await response.json();
    const products = Array.isArray(data.products) ? data.products : [];
    track.replaceChildren(...products.map((product) => buildCard(root, product)));
    return products.length;
  } catch (error) {
    console.warn('[cv3-product-recommendations]', error);
    track.replaceChildren();
    return 0;
  } finally {
    track.removeAttribute('aria-busy');
  }
}

/** Adds one unit of a single-variant product through the store's cart endpoint and waits for confirmation. */
async function addToCart(root, button) {
  if (button.dataset.busy === 'true') return;
  const status = root.querySelector('[data-cv3-prc-status]');
  button.dataset.busy = 'true';
  button.setAttribute('aria-busy', 'true');
  const original = button.textContent;
  button.textContent = root.dataset.adding;
  const deferred = CartLinesUpdateEvent.createPromise();
  const variantId = button.dataset.variantId;
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: variantId, quantity: 1 }],
    promise: deferred.promise,
  }));
  try {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    const addResponse = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const payload = await addResponse.json();
    if (!addResponse.ok || payload.status) throw new Error(payload.description || payload.message || 'add failed');
    const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, source: 'cv3-product-recommendations', sourceId: root.id || '', itemCount: 1, productId: button.closest('[data-product-id]')?.dataset.productId },
    });
    button.textContent = root.dataset.added;
    if (status) status.textContent = root.dataset.added;
    openCartDrawer();
  } catch (error) {
    console.warn('[cv3-product-recommendations] add to cart failed', error);
    let cart;
    try {
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json());
    } catch (cartError) {
      cart = undefined;
    }
    deferred.resolve({ cart, detail: { didError: true, source: 'cv3-product-recommendations', sourceId: root.id || '', itemCount: 1, productId: button.closest('[data-product-id]')?.dataset.productId } });
    if (status) status.textContent = root.dataset.addError;
    button.textContent = original;
  } finally {
    setTimeout(() => {
      delete button.dataset.busy;
      button.removeAttribute('aria-busy');
      if (button.textContent === root.dataset.added) button.textContent = original;
    }, 1500);
  }
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer') || document.querySelector('theme-drawer');
  if (drawer && typeof drawer.open === 'function') drawer.open();
}

function destroyRoot(root) {
  const instance = instances.get(root);
  instance?.destroy();
  instances.delete(root);
}

async function initRoot(root) {
  destroyRoot(root);
  const carousel = new Carousel(root);
  instances.set(root, carousel);
  if (root.dataset.source === 'auto') {
    const count = await loadRecommendations(root);
    if (count === 0) {
      root.hidden = true;
      return;
    }
  }
  carousel.init();
}

function initAll(scope = document) {
  const roots = [scope, ...(scope.querySelectorAll ? scope.querySelectorAll('[data-cv3-prc]') : [])];
  roots.filter((root) => root.matches && root.matches('[data-cv3-prc]')).forEach((root) => {
    if (!root.dataset.cv3PrcBound) {
      root.dataset.cv3PrcBound = 'true';
      root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cv3-prc-add]');
        if (button && root.contains(button)) {
          event.preventDefault();
          addToCart(root, button);
        }
      });
    }
    initRoot(root);
  });
}

if (!window.__cv3PrcLoaded) {
  window.__cv3PrcLoaded = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [event.target, ...(event.target.querySelectorAll ? event.target.querySelectorAll('[data-cv3-prc]') : [])];
    roots.filter((root) => root.matches && root.matches('[data-cv3-prc]')).forEach(destroyRoot);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-cv3-prc]');
    if (root) instances.get(root)?.pauseFor(60000);
  });
}
